/**
 * ENDOMARKETING — peças usadas pelas quatro telas (painel, aniversariantes,
 * comunicado, fotos e eventos). Ficam aqui para as telas não repetirem regra
 * de negócio: como o número vira link do WhatsApp, como a foto é compactada
 * antes de subir, quais são os modelos de mensagem da empresa.
 */

export interface Colaborador {
  id: number;
  nome: string;
  cargo: string | null;
  departamento: string | null;
  telefone: string | null;
  foto: string | null;
  data_nascimento?: string | null;
  data_admissao?: string | null;
  dia?: number;
  /** Anos que a pessoa completa NESTE ano (vem calculado do servidor). */
  fazendo?: number;
  anos?: number;
  faltam?: number;
  parabenizado?: boolean;
}

export const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

export const MES_CURTO = [
  "jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez",
];

/** Compara ignorando acento e caixa (busca das listas). */
export function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

/** AAAA-MM-DD → DD/MM/AAAA (o resto passa direto). */
export function fmt(s: string | null | undefined): string {
  if (!s) return "—";
  const d = String(s).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split("-").reverse().join("/") : d;
}

export function primeiroNome(nome: string): string {
  return String(nome ?? "").trim().split(/\s+/)[0] ?? "";
}

/** Iniciais para quando ainda não há foto. */
export function iniciais(nome: string): string {
  const p = String(nome ?? "").trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p.length > 1 ? p[p.length - 1][0] : "")).toUpperCase();
}

export function temTelefone(c: { telefone?: string | null }): boolean {
  return !!String(c.telefone ?? "").replace(/\D/g, "");
}

/**
 * Telefone do cadastro → número no formato do wa.me (só dígitos, com DDI).
 *
 * Número sem DDI recebe o 55 do Brasil — é como o telefone é cadastrado aqui;
 * o zero da frente ("0 11 9…", o jeito antigo de discar o DDD) sai antes, senão
 * o número viraria outro. Sem dígito nenhum devolve "": quem chama decide o que
 * dizer na tela (é o "sem telefone no cadastro").
 */
export function normalizarNumero(telefone: string | null | undefined): string {
  const numero = String(telefone ?? "").replace(/\D/g, "").replace(/^0+/, "");
  if (!numero) return "";
  return numero.length <= 11 ? `55${numero}` : numero;
}

/**
 * Link do WhatsApp com o texto pronto. Sem número, o WhatsApp abre para a
 * PESSOA escolher a conversa — é o caminho do "enviar para um grupo" (o wa.me
 * não aceita endereço de grupo).
 */
export function linkWhatsApp(numero: string, texto: string): string {
  return `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;
}

/**
 * Abre o link numa aba nova e diz se ABRIU (false = o bloqueador de pop-up
 * barrou).
 *
 * Não passa "noopener" no window.open de propósito: com ele o navegador
 * devolve null SEMPRE — mesmo abrindo —, e a tela não tinha como distinguir
 * aba aberta de aba bloqueada (era o que fazia a fila do Comunicado acusar
 * "bloqueou a janela" a cada clique e nunca andar). O vínculo com esta página
 * é cortado logo em seguida (opener = null), que é o que o noopener protegia.
 */
export function abrirJanela(url: string): boolean {
  const janela = window.open(url, "_blank");
  if (!janela) return false;
  try {
    janela.opener = null;
  } catch {
    /* navegador que não deixa mexer: a aba abriu do mesmo jeito */
  }
  return true;
}

/**
 * Abre a conversa da pessoa no WhatsApp com o texto pronto.
 * Devolve false quando não há número: a tela avisa em vez de abrir aba vazia.
 */
export function abrirWhatsApp(telefone: string | null | undefined, texto: string): boolean {
  const numero = normalizarNumero(telefone);
  if (!numero) return false;
  window.open(linkWhatsApp(numero, texto), "_blank", "noopener");
  return true;
}

/** Troca {nome} e {setor} pelos dados da pessoa (é o que personaliza o disparo). */
export function aplicarVariaveis(texto: string, c: Colaborador): string {
  return String(texto ?? "")
    .replace(/\{nome\}/gi, primeiroNome(c.nome))
    .replace(/\{setor\}/gi, c.departamento ?? "")
    .replace(/\{cargo\}/gi, c.cargo ?? "")
    .replace(/\{empresa\}/gi, "SERCAMP");
}

/**
 * O texto que vai para um GRUPO: não há uma pessoa para pôr no {nome}, então
 * ele vira "pessoal" ("Olá, pessoal!"), e {setor} vira o setor escolhido na
 * tela (ou some). Sem isto o grupo receberia "Olá, {nome}!" cru.
 */
export function textoParaGrupo(texto: string, setor = ""): string {
  return String(texto ?? "")
    .replace(/\{nome\}/gi, "pessoal")
    .replace(/\{setor\}/gi, setor)
    .replace(/\{cargo\}/gi, "")
    .replace(/\{empresa\}/gi, "SERCAMP")
    .replace(/[ \t]{2,}/g, " ")
    // {setor}/{cargo} vazios deixariam "do setor ." — o espaço antes da pontuação sai.
    .replace(/[ \t]+([.,;:!?])/g, "$1");
}

/** Copia para a área de transferência (para colar no grupo do WhatsApp). */
export async function copiar(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}

/** 12345678 → "11,8 MB" (é assim que a tela fala de tamanho de arquivo). */
export function tamanhoLegivel(bytes: number | null | undefined): string {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1048576).toFixed(1).replace(".", ",")} MB`;
}

/**
 * `compactar` com a mensagem certa para foto de iPhone: o HEIC abre no Safari,
 * mas não no Chrome do computador — e "arquivo de imagem inválido" deixaria a
 * pessoa sem saber o que fazer.
 */
export async function compactarImagem(file: File, max: number, quality: number): Promise<string> {
  const heic = /\.(heic|heif)$/i.test(file.name) || /hei[cf]/i.test(file.type);
  if (!file.type.startsWith("image/") && !heic) {
    throw new Error(`"${file.name}" não é uma imagem.`);
  }
  try {
    return await compactar(file, max, quality);
  } catch (e) {
    if (heic) {
      throw new Error(
        `"${file.name}" é foto do iPhone (HEIC), que este navegador não abre. ` +
          "Envie a foto em JPG, ou escolha pelo próprio iPhone.",
      );
    }
    throw e;
  }
}

/** Compacta a imagem no aparelho (a foto de perfil vai como dataURL). */
export function compactar(file: File, max = 480, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Arquivo de imagem inválido."));
      img.onload = () => {
        const escala = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * escala);
        canvas.height = Math.round(img.height * escala);
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Não foi possível processar a imagem."));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

/* ------------------------------------------------------------------ *
 *  Modelos de mensagem                                                 *
 * ------------------------------------------------------------------ */

export interface ModeloMensagem {
  key: string;
  nome: string;
  titulo: string;
  texto: string;
}

/**
 * Textos prontos para o RH não começar do zero (e para o tom da empresa ficar
 * o mesmo em todo comunicado). Todos são editáveis antes de disparar.
 */
export const MODELOS_MENSAGEM: ModeloMensagem[] = [
  {
    key: "aniversario",
    nome: "Aniversário",
    titulo: "Feliz aniversário",
    texto:
      "Parabéns, {nome}! 🎉\n\nA equipe SERCAMP deseja um feliz aniversário e um ano muito especial. Obrigado por fazer parte do nosso time!",
  },
  {
    key: "empresa",
    nome: "Aniversário de empresa",
    titulo: "Tempo de casa",
    texto:
      "Parabéns, {nome}! 🎊\n\nHoje você completa mais um ano de SERCAMP. Obrigado pela dedicação e por caminhar com a gente!",
  },
  {
    key: "seguranca",
    nome: "Campanha de segurança",
    titulo: "Campanha de segurança",
    texto:
      "Olá, {nome}! 🦺\n\nLembrete da SERCAMP: o uso de EPI é obrigatório em toda atividade de campo. Antes de iniciar o serviço, confira capacete, luva isolante, óculos e o vestimenta adequada.\n\nSegurança em primeiro lugar — sempre.",
  },
  {
    key: "reuniao",
    nome: "Aviso de reunião",
    titulo: "Reunião",
    texto:
      "Olá, {nome}!\n\nTemos reunião do setor {setor} no dia __/__ às __h, na sede da SERCAMP. Sua presença é importante.\n\nQualquer imprevisto, avise o RH.",
  },
  {
    key: "treinamento",
    nome: "Convocação de treinamento",
    titulo: "Treinamento",
    texto:
      "Olá, {nome}!\n\nVocê está convocado(a) para o treinamento de ____________ no dia __/__, das __h às __h.\n\nLeve documento com foto. A presença conta para a sua ficha de treinamentos.",
  },
  {
    key: "documento",
    nome: "Pendência de documento",
    titulo: "Documentos",
    texto:
      "Olá, {nome}!\n\nEstá faltando um documento seu no RH: ____________. Pode enviar por aqui mesmo ou entregar na sede até o dia __/__?\n\nObrigado!",
  },
  {
    key: "resultado",
    nome: "Resultado do mês",
    titulo: "Resultado do mês",
    texto:
      "Olá, {nome}!\n\nFechamos mais um mês e queremos dividir o resultado com quem fez acontecer: obrigado pelo empenho da equipe {setor}.\n\nSeguimos juntos! 💚",
  },
  {
    key: "evento",
    nome: "Convite de evento",
    titulo: "Convite",
    texto:
      "Olá, {nome}! 🎈\n\nVocê está convidado(a) para ____________, no dia __/__ às __h, em ____________.\n\nConfirme a presença respondendo esta mensagem.",
  },
];

/* ------------------------------------------------------------------ *
 *  Cartão de aniversário (imagem para postar no grupo)                 *
 * ------------------------------------------------------------------ */

/**
 * Desenha um cartão quadrado (1080×1080) com a foto, o nome e a marca — o que
 * o RH costuma montar à mão em editor de imagem para postar no grupo. Feito em
 * canvas, sem depender de nada externo (a tela roda offline na obra também).
 *
 * `foto` é a dataURL do cadastro (ou a trocada só para este post); sem foto,
 * entram as iniciais num círculo.
 *
 * `fundo` é o endereço de um FUNDO salvo (o modelo do mês feito no Canva, por
 * exemplo). Ele entra em modo "cover" e ganha por cima um véu escuro em
 * degradê — sem o véu, o texto branco some num fundo claro. O desenho por cima
 * é o MESMO do cartão verde, para o post ter a cara da empresa em qualquer
 * fundo. Se o fundo não carregar (apagado, sem rede), o cartão sai no verde.
 * O endereço tem de ser desta mesma origem: imagem de outro site "suja" o
 * canvas e o navegador recusa exportar o PNG.
 */
export async function gerarCartaoAniversario(opcoes: {
  nome: string;
  foto: string | null;
  legenda: string;
  titulo?: string;
  fundo?: string | null;
}): Promise<string> {
  const { nome, foto, legenda, titulo = "Feliz aniversário!", fundo = null } = opcoes;
  const L = 1080;

  const canvas = document.createElement("canvas");
  canvas.width = L;
  canvas.height = L;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Não foi possível gerar o cartão.");

  // As duas imagens carregam juntas; a que falhar vira null (e o cartão sai
  // mesmo assim — com o verde no lugar do fundo, com as iniciais no da foto).
  const [imgFundo, imgFoto] = await Promise.all([
    fundo ? carregarImagem(fundo) : Promise.resolve(null),
    foto ? carregarImagem(foto) : Promise.resolve(null),
  ]);

  if (imgFundo) {
    desenharCover(ctx, imgFundo, 0, 0, L, L);
    // Véu escuro (verde-quase-preto da marca): leve no alto, onde está a
    // foto, e forte embaixo, onde ficam o nome e a assinatura.
    const veu = ctx.createLinearGradient(0, 0, 0, L);
    veu.addColorStop(0, "rgba(4,20,12,0.28)");
    veu.addColorStop(0.45, "rgba(4,20,12,0.36)");
    veu.addColorStop(0.62, "rgba(4,20,12,0.62)");
    veu.addColorStop(1, "rgba(4,20,12,0.84)");
    ctx.fillStyle = veu;
    ctx.fillRect(0, 0, L, L);
  } else {
    desenharFundoMarca(ctx, L);
  }

  // Moldura circular da foto.
  const cx = L / 2;
  const cy = 380;
  const raio = 200;
  ctx.beginPath();
  ctx.arc(cx, cy, raio + 12, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.22)";
  ctx.fill();

  if (imgFoto) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, raio, 0, Math.PI * 2);
    ctx.clip();
    // "cover": recorta o excedente em vez de achatar o rosto.
    desenharCover(ctx, imgFoto, cx - raio, cy - raio, raio * 2, raio * 2);
    ctx.restore();
  } else {
    desenharIniciais(ctx, cx, cy, raio, nome);
  }

  ctx.textAlign = "center";
  // Em fundo de foto, uma sombra curta segura a leitura onde o véu não basta.
  if (imgFundo) {
    ctx.shadowColor = "rgba(0,0,0,0.45)";
    ctx.shadowBlur = 14;
  }

  ctx.fillStyle = "#eafff2";
  ctx.font = "600 44px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText(titulo.toUpperCase(), L / 2, 700);

  // Nome: diminui a fonte até caber na largura útil.
  let tamanho = 86;
  ctx.font = `800 ${tamanho}px system-ui, -apple-system, Segoe UI, sans-serif`;
  while (ctx.measureText(nome).width > L - 140 && tamanho > 40) {
    tamanho -= 4;
    ctx.font = `800 ${tamanho}px system-ui, -apple-system, Segoe UI, sans-serif`;
  }
  ctx.fillStyle = "#ffffff";
  ctx.fillText(nome, L / 2, 800);

  ctx.fillStyle = "rgba(234,255,242,0.85)";
  ctx.font = "500 38px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText(legenda, L / 2, 862);

  // Assinatura da empresa.
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 52px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText("SERCAMP", L / 2, 985);
  ctx.fillStyle = "rgba(234,255,242,0.7)";
  ctx.font = "500 26px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText("Engenharia e Manutenção Elétrica", L / 2, 1025);

  return canvas.toDataURL("image/png");
}

/** O degradê verde da marca com as bolhas de luz (o fundo padrão do cartão). */
function desenharFundoMarca(ctx: CanvasRenderingContext2D, L: number) {
  const fundo = ctx.createLinearGradient(0, 0, L, L);
  fundo.addColorStop(0, "#06351e");
  fundo.addColorStop(0.55, "#0c7a3f");
  fundo.addColorStop(1, "#15a85a");
  ctx.fillStyle = fundo;
  ctx.fillRect(0, 0, L, L);

  // Bolhas de luz (mesma linguagem visual do fundo do sistema).
  for (const [x, y, r, a] of [
    [880, 180, 260, 0.16],
    [180, 900, 320, 0.12],
    [980, 940, 200, 0.1],
  ] as const) {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(52,210,126,${a})`);
    g.addColorStop(1, "rgba(52,210,126,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
}

/** Desenha a imagem cobrindo a área inteira ("cover"): corta o que sobra, nunca achata. */
function desenharCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  const escala = Math.max(w / img.width, h / img.height);
  const dw = img.width * escala;
  const dh = img.height * escala;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/** Carrega a imagem; null quando não abre (o cartão segue sem ela). */
function carregarImagem(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.width && img.height ? img : null);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function desenharIniciais(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  raio: number,
  nome: string,
) {
  ctx.beginPath();
  ctx.arc(cx, cy, raio, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.16)";
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 150px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(iniciais(nome), cx, cy + 6);
  ctx.textBaseline = "alphabetic";
}

/**
 * Dispara o download de uma dataURL com o nome escolhido. Serve também para
 * endereço desta mesma origem (o arquivo guardado no servidor).
 */
export function baixarDataUrl(dataUrl: string, nomeArquivo: string) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = nomeArquivo;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Baixa um arquivo que já está na memória (vídeo/PDF anexado, por exemplo). */
export function baixarArquivo(arquivo: Blob, nomeArquivo: string) {
  const url = URL.createObjectURL(arquivo);
  baixarDataUrl(url, nomeArquivo);
  // O download já começou; o endereço temporário pode ser solto depois.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/* ------------------------------------------------------------------ *
 *  MÍDIA do aviso (fotos, vídeos e PDF que vão junto da mensagem)      *
 * ------------------------------------------------------------------ */

/** Quantas mídias um aviso leva (o servidor confere o mesmo número). */
export const MAX_MIDIAS = 10;
/** Teto de vídeo e PDF (imagem é compactada antes e fica bem abaixo). */
export const MAX_MIDIA_MB = 60;

export type TipoMidiaTela = "imagem" | "video" | "pdf";

/** Extensão que o servidor aceita, pelo tipo do arquivo. */
const EXTENSAO_POR_TIPO: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/x-m4v": "m4v",
  "video/3gpp": "3gp",
  "video/webm": "webm",
  "application/pdf": "pdf",
};
const EXT_IMAGEM = /\.(jpe?g|png|webp|gif|heic|heif)$/i;
const EXT_VIDEO = /\.(mp4|mov|m4v|3gp|webm)$/i;

/** Foto, vídeo ou PDF? Pelo tipo do arquivo e, na falta dele, pela extensão. */
export function tipoDaMidia(arquivo: { name: string; type: string }): TipoMidiaTela | null {
  const t = (arquivo.type || "").toLowerCase();
  if (t.startsWith("image/")) return "imagem";
  if (t.startsWith("video/")) return "video";
  if (t === "application/pdf") return "pdf";
  if (EXT_IMAGEM.test(arquivo.name)) return "imagem";
  if (EXT_VIDEO.test(arquivo.name)) return "video";
  if (/\.pdf$/i.test(arquivo.name)) return "pdf";
  return null;
}

/**
 * Garante que o nome do arquivo termina com uma extensão que o servidor aceita.
 * Alguns seletores de arquivo do Android entregam o vídeo com nome sem
 * extensão ("video:1234") — o tipo do arquivo diz o que ele é.
 */
export function comExtensaoCerta(arquivo: File): File {
  if (EXT_IMAGEM.test(arquivo.name) || EXT_VIDEO.test(arquivo.name) || /\.pdf$/i.test(arquivo.name)) {
    return arquivo;
  }
  const ext = EXTENSAO_POR_TIPO[(arquivo.type || "").toLowerCase()];
  if (!ext) return arquivo;
  const base = arquivo.name.replace(/[^\p{L}\p{N}._ -]+/gu, "-").replace(/\.[^.]*$/, "") || "arquivo";
  // Pelo slice (e não o File inteiro): é o que garante o NOME novo em todo
  // lugar — há motor que, recebendo um File, conserva o nome antigo.
  return new File([arquivo.slice(0, arquivo.size, arquivo.type)], `${base}.${ext}`, { type: arquivo.type });
}

/* ------------------------------------------------------------------ *
 *  ARTE DO COMUNICADO (a imagem que vai junto da mensagem)             *
 * ------------------------------------------------------------------ *
 * O link do WhatsApp (wa.me) carrega SÓ TEXTO — não existe parâmetro de
 * imagem. Para a arte ir junto, o caminho é o próprio navegador:
 *
 *   • celular  → compartilhamento do sistema (navigator.share com arquivo):
 *                o WhatsApp recebe a IMAGEM e o texto vira a LEGENDA dela;
 *   • computador → a arte vai para a área de transferência e a pessoa cola
 *                  (Ctrl+V) na conversa aberta pelo wa.me;
 *   • sempre   → baixar a imagem e anexar à mão.
 *
 * Quem decide qual caminho aparece na tela é a checagem abaixo, feita no
 * aparelho de quem está usando — nunca "adivinhando" pelo nome do navegador.  */

/** Estilos de arte (só a cor muda; o desenho é o mesmo). */
export const ESTILOS_ARTE = [
  { key: "marca", nome: "Verde SERCAMP", de: "#06351e", meio: "#0c7a3f", ate: "#15a85a", brilho: "52,210,126" },
  { key: "alerta", nome: "Segurança (âmbar)", de: "#4a2c00", meio: "#b45309", ate: "#f59e0b", brilho: "251,191,36" },
  { key: "festa", nome: "Festa (roxo)", de: "#2a1147", meio: "#6d28d9", ate: "#a855f7", brilho: "196,181,253" },
] as const;

export type EstiloArte = (typeof ESTILOS_ARTE)[number]["key"];

/**
 * Desenha a ARTE do comunicado (1080×1080) com o título e o texto da mensagem.
 *
 * É o mesmo canvas do cartão de aniversário — sem biblioteca, sem servidor —
 * porque a tela precisa funcionar no computador da sede e no celular na obra.
 * O texto é quebrado por largura e cortado quando não cabe: arte de WhatsApp é
 * chamada, não é ofício; o texto completo vai na mensagem.
 */
export function gerarArteComunicado(opcoes: {
  titulo: string;
  texto: string;
  estilo?: EstiloArte;
  /** Assinatura do rodapé (padrão: a empresa). */
  assinatura?: string;
}): Promise<string> {
  const { titulo, texto, estilo = "marca", assinatura = "SERCAMP" } = opcoes;
  const cores = ESTILOS_ARTE.find((e) => e.key === estilo) ?? ESTILOS_ARTE[0];
  const L = 1080;

  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = L;
    canvas.height = L;
    const ctx = canvas.getContext("2d");
    if (!ctx) return reject(new Error("Não foi possível gerar a arte."));

    const fundo = ctx.createLinearGradient(0, 0, L, L);
    fundo.addColorStop(0, cores.de);
    fundo.addColorStop(0.55, cores.meio);
    fundo.addColorStop(1, cores.ate);
    ctx.fillStyle = fundo;
    ctx.fillRect(0, 0, L, L);

    for (const [x, y, r, a] of [
      [880, 160, 280, 0.16],
      [160, 920, 340, 0.12],
      [1000, 980, 220, 0.1],
    ] as const) {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, `rgba(${cores.brilho},${a})`);
      g.addColorStop(1, `rgba(${cores.brilho},0)`);
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }

    // Faixa clara atrás do texto: garante leitura mesmo no degradê claro.
    ctx.fillStyle = "rgba(0,0,0,0.18)";
    ctx.fillRect(0, 250, L, 560);

    ctx.textAlign = "center";

    // Título: diminui até caber em duas linhas.
    const margem = 110;
    let tam = 78;
    let linhasTitulo: string[] = [];
    do {
      ctx.font = `800 ${tam}px system-ui, -apple-system, Segoe UI, sans-serif`;
      linhasTitulo = quebrarLinhas(ctx, titulo.trim() || "Comunicado", L - margem * 2);
      if (linhasTitulo.length <= 2) break;
      tam -= 6;
    } while (tam > 42);
    linhasTitulo = linhasTitulo.slice(0, 2);

    ctx.fillStyle = "#ffffff";
    let y = 250 + 110;
    for (const linha of linhasTitulo) {
      ctx.fillText(linha, L / 2, y);
      y += tam + 12;
    }

    // Régua embaixo do título.
    ctx.fillStyle = "rgba(255,255,255,0.55)";
    ctx.fillRect(L / 2 - 90, y - 26, 180, 5);
    y += 46;

    // Corpo: até 7 linhas do texto (sem as variáveis, que são do texto pessoal).
    ctx.font = "500 40px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.94)";
    const corpo = texto
      .replace(/\{[^}]+\}/g, "") // {nome}/{setor} não fazem sentido numa arte única
      .replace(/\s*\n\s*/g, " ")
      .trim();
    const linhas = quebrarLinhas(ctx, corpo, L - margem * 2).slice(0, 7);
    for (const [i, linha] of linhas.entries()) {
      const ultima = i === linhas.length - 1;
      const cortou = ultima && corpo.length > linhas.join(" ").length + 2;
      ctx.fillText(cortou ? `${linha}…` : linha, L / 2, y);
      y += 54;
    }

    // Assinatura.
    ctx.fillStyle = "#ffffff";
    ctx.font = "800 54px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText(assinatura, L / 2, 985);
    ctx.fillStyle = "rgba(255,255,255,0.72)";
    ctx.font = "500 26px system-ui, -apple-system, Segoe UI, sans-serif";
    ctx.fillText("Engenharia e Manutenção Elétrica", L / 2, 1025);

    resolve(canvas.toDataURL("image/png"));
  });
}

/** Quebra o texto em linhas que cabem na largura (medindo no próprio canvas). */
function quebrarLinhas(ctx: CanvasRenderingContext2D, texto: string, largura: number): string[] {
  const palavras = String(texto ?? "").split(/\s+/).filter(Boolean);
  const linhas: string[] = [];
  let atual = "";
  for (const p of palavras) {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (ctx.measureText(tentativa).width > largura && atual) {
      linhas.push(atual);
      atual = p;
    } else {
      atual = tentativa;
    }
  }
  if (atual) linhas.push(atual);
  return linhas;
}

/** dataURL → File, que é o formato que o compartilhamento do sistema aceita. */
export async function dataUrlParaArquivo(dataUrl: string, nome: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], nome, { type: blob.type || "image/png" });
}

/**
 * Este aparelho consegue mandar o(s) ARQUIVO(S) pelo compartilhamento do sistema?
 *
 * Pergunta ao próprio navegador (`canShare` com os arquivos em mãos) em vez de
 * deduzir por celular/computador: é o único jeito honesto, porque o suporte
 * muda por navegador, por versão, por tipo de arquivo (vídeo e PDF nem sempre
 * passam) e exige HTTPS. Com `false`, a tela oferece o caminho de copiar/baixar
 * em vez de prometer o que não vai acontecer.
 */
export function podeCompartilharArquivo(arquivo: File | File[]): boolean {
  const files = Array.isArray(arquivo) ? arquivo : [arquivo];
  if (files.length === 0) return false;
  try {
    return typeof navigator !== "undefined" && typeof navigator.canShare === "function"
      ? navigator.canShare({ files })
      : false;
  } catch {
    return false;
  }
}

/**
 * "Usar o WhatsApp Web neste aparelho" — a saída para quando a bandeja do
 * sistema não serve.
 *
 * O Chrome do WINDOWS também diz que compartilha arquivo (conferido: imagem,
 * mp4, mov e PDF), e aí a tela manda para a bandeja do Windows — que só lista
 * o WhatsApp se o aplicativo da loja estiver instalado. Quem usa o WhatsApp
 * Web no navegador ficaria sem caminho. Com esta opção ligada, o envio segue o
 * caminho do computador (copiar/baixar a mídia e abrir a conversa pelo link).
 * Fica lembrada por aparelho; try/catch porque aba anônima lança.
 */
const CHAVE_WHATSAPP_WEB = "sercamp-rh-endo-whatsapp-web";

export function lerUsarWhatsAppWeb(): boolean {
  try {
    return localStorage.getItem(CHAVE_WHATSAPP_WEB) === "1";
  } catch {
    return false;
  }
}

export function guardarUsarWhatsAppWeb(ligado: boolean) {
  try {
    if (ligado) localStorage.setItem(CHAVE_WHATSAPP_WEB, "1");
    else localStorage.removeItem(CHAVE_WHATSAPP_WEB);
  } catch {
    /* aba anônima / armazenamento bloqueado: vale só enquanto a tela estiver aberta */
  }
}

/**
 * Abre o compartilhamento do sistema com o(s) arquivo(s) e o texto. No
 * WhatsApp, a mídia entra com o texto como LEGENDA — é o "tudo junto" que o
 * wa.me não faz. Devolve false quando a pessoa cancela (não é erro).
 *
 * Tem de ser chamada logo no clique, com os arquivos JÁ prontos: o navegador
 * só abre a bandeja enquanto o clique está "fresco" — esperar o servidor
 * antes faz o celular recusar.
 */
export async function compartilharArte(arquivo: File | File[], texto: string): Promise<boolean> {
  try {
    await navigator.share({ files: Array.isArray(arquivo) ? arquivo : [arquivo], text: texto });
    return true;
  } catch (e) {
    // AbortError = a pessoa fechou a bandeja; qualquer outro erro sobe.
    if ((e as Error)?.name === "AbortError") return false;
    throw e;
  }
}

/**
 * Copia a IMAGEM para a área de transferência (para colar no WhatsApp Web com
 * Ctrl+V). Só PNG: é o formato que os navegadores aceitam na área de
 * transferência. Devolve false quando o navegador não deixa.
 */
export async function copiarImagem(dataUrl: string): Promise<boolean> {
  try {
    if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) return false;
    const blob = await (await fetch(dataUrl)).blob();
    const png = blob.type === "image/png" ? blob : await paraPng(blob);
    await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
    return true;
  } catch {
    return false;
  }
}

/** Converte qualquer imagem para PNG (a área de transferência só aceita PNG). */
function paraPng(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Não foi possível converter a imagem."));
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Falha ao converter."))), "image/png");
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Imagem inválida."));
    };
    img.src = url;
  });
}
